import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import { getCompanySettings } from 'modules/settings/stores/company-settings-slice-handler';
import sdkClient from 'utils/sdk-client';
import t from 'utils/translate';
import Log from 'utils/log';
import { fileSizeToHumanFormat } from 'utils/functions';
import { getLoggedInUser } from 'modules/contacts/stores/contacts-slice-handler';

const initialState = {
    data: null,
    progress: 0,
    quotaWarning: null,
};

class StorageQuotaSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!StorageQuotaSliceHandler.instance) {
            StorageQuotaSliceHandler.instance = new StorageQuotaSliceHandler();
        }

        return StorageQuotaSliceHandler.instance;
    }

    constructor() {
        super('storageQuotaSlice');

        sdkClient.setSocketEventListener('ATTACHMENTS_LIST_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('ATTACHMENTS_DELETE_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('PROCESSES_LIST_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('PROCESS_UPDATE_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('PROCESS_SAVE_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('PROCESS_OWNER_CHANGE_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('PROCESS_DELETE_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('EXTERNAL_PROCESS_UPDATE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('EXTERNAL_PROCESS_DELETE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('CONVERSATION_DELETE_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('CONVERSATIONS_LIST_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('CONVERSATIONS_LIST_WITH_ATTACHMENTS_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('MESSAGE_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('MESSAGE_DELETE_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('EXTERNAL_MESSAGE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('EXTERNAL_MESSAGE_DELETE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('EXTERNAL_CONVERSATION_DELETE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('EXTERNAL_PROCESS_OWNER_CHANGE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('QUOTA_UPDATE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('CONVERSATIONS_LIST_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('CONVERSATIONS_LIST_RESPONSE', (e) => this.onSocketEvent(e));
        sdkClient.setSocketEventListener('ORGANIZATION_DATA_RESPONSE', (cugData) => this.parseCugData(cugData));
        sdkClient.setSocketEventListener('ORGANIZATION_DATA_UPDATE', (cugData) => this.parseCugData(cugData));

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                setData(state, action) {
                    state.data = action.payload;
                },
                setPartialData(state, action) {
                    state.data = { ...state.data, ...action.payload };
                },
                setThresholdData(state, action) {
                    state.data.threshold = { ...state.data.threshold, ...action.payload };
                },
                setAttachmentData(state, action) {
                    state.data.attachment = { ...state.data.attachment, ...action.payload };
                },
                setProcessData(state, action) {
                    state.data.process = { ...state.data.process, ...action.payload };
                },
                setProgress(state, action) {
                    state.progress = action.payload;
                },
                setQuotaWarning(state, action) {
                    state.quotaWarning = action.payload;
                },
            },
        });
    }

    onSocketEvent(e) {
        if (typeof e.quota === 'undefined' || !e.quota.attachment || !e.quota.process) {
            return;
        }

        const receivedData = e.quota;
        const data = this.getOwnState().data;

        if (data == null) {
            this.dispatch('setData', receivedData);
        } else {
            if (data.adminEmail !== receivedData.adminEmail) {
                Log.debug('STORAGE QUOTA STORE', `The admin email has been updated: ${receivedData.adminEmail}`);
                this.dispatch('setPartialData', { adminEmail: receivedData.adminEmail });
            }
            if (data.maxSize !== receivedData.maxSize) {
                Log.debug('STORAGE QUOTA STORE', `The quota maxSize has been updated: ${receivedData.maxSize}`);
                this.dispatch('setPartialData', { maxSize: receivedData.maxSize });
            }
            if (data.threshold.alert !== receivedData.threshold.alert) {
                Log.debug(
                    'STORAGE QUOTA STORE',
                    `The quota threshold alert has been updated: ${receivedData.threshold.alert}`,
                );
                this.dispatch('setThresholdData', { alert: receivedData.threshold.alert });
            }
            if (data.threshold.warning !== receivedData.threshold.warning) {
                Log.debug(
                    'STORAGE QUOTA STORE',
                    `The quota threshold warning has been updated: ${receivedData.threshold.warning}`,
                );
                this.dispatch('setThresholdData', { warning: receivedData.threshold.warning });
            }
            if (data.attachment.actualSize !== receivedData.attachment.actualSize) {
                Log.debug(
                    'STORAGE QUOTA STORE',
                    `The quota attachment.actualSize has been updated: ${receivedData.attachment.actualSize}`,
                );
                this.dispatch('setAttachmentData', { actualSize: receivedData.attachment.actualSize });
            }
            if (data.process.actualSize !== receivedData.process.actualSize) {
                Log.debug(
                    'STORAGE QUOTA STORE',
                    `The quota process.actualSize has been updated: ${receivedData.process.actualSize}`,
                );
                this.dispatch('setProcessData', { actualSize: receivedData.process.actualSize });
            }
        }

        this.computeProgress(receivedData);
    }

    computeProgress(receivedData) {
        const actualSize = receivedData.attachment.actualSize + receivedData.process.actualSize;
        let percentage = (actualSize / receivedData.maxSize) * 100;

        // Prevent layout overflow
        if (percentage > 100) {
            percentage = 100;
        }

        this.dispatch('setProgress', percentage);

        if (percentage > receivedData.threshold.alert) {
            const quotaWarning = {
                usedSpace: actualSize,
                totalSpace: receivedData.maxSize,
            };
            this.dispatch('setQuotaWarning', quotaWarning);
        } else {
            this.dispatch('setQuotaWarning', null);
        }
    }

    parseCugData(e) {
        if (typeof e.CugSettingsRare !== 'undefined' && typeof e.CugSettingsRare.CloudStorageEnabled !== 'undefined') {
            if (!e.CugSettingsRare.CloudStorageEnabled) {
                this.dispatch('setQuotaWarning', null);
            }

            const cugData = e.CugSettingsRare.CloudStorageSettings.Quota;
            e.quota = {
                ...this.getOwnState().data,
                adminEmail: cugData.AdminEmail,
                // in CUG comes in MB; in other events is in KB
                maxSize: cugData.MaxSize * 1024 * 1024,
                threshold: {
                    warning: cugData.Thresholds.Warning,
                    alert: cugData.Thresholds.Alert,
                },
            };
        }
        this.onSocketEvent(e);
    }

    sendEmail() {
        window.location.href = this.getStorageQuotaUpgradeMailLink();
    }

    getStorageQuotaUpgradeMailLink() {
        const OrganizationLabel = getCompanySettings().OrganizationLabel;
        const data = this.getOwnState().data;
        const actualSizeBytes = data.process.actualSize + data.attachment.actualSize;
        const totalUsedSizeBytes = fileSizeToHumanFormat(actualSizeBytes, 2, 'after');
        const messageSubject = t('js_all_request_upgrade_storage_quota_subject');
        const messageBody = t('js_all_request_upgrade_storage_quota_body')
            .replace('%currentQuota', totalUsedSizeBytes.sizeWithLabel)
            .replace('%organisationName', OrganizationLabel)
            .replace('%user', getLoggedInUser().ComputedUserName)
            .replace('%msisdn', getLoggedInUser().Msisdn)
            // TODO: implement getDisplayPhoneNumber when configs become available
            // .replace("%msisdn", $.getDisplayPhoneNumber(LoggedInMsisdn))
            .replace(/,/g, '%2C')
            .replace(/%newLine/g, '%0D%0A');

        return `mailto:${data.adminEmail}?subject=${messageSubject}&body=${messageBody}`;
    }

    requestQuotaInfo = () => {
        sdkClient.emitSocketEvent('QUOTA_INFO_REQUEST');
    };
}

export const storageQuotaSliceHandler = StorageQuotaSliceHandler.getInstance();
export const selectStorageQuotaData = (state) => state.storageQuotaSlice.data;
export const selectStorageQuotaProgress = (state) => state.storageQuotaSlice.progress;
export const selectStorageQuotaWarning = (state) => state.storageQuotaSlice.quotaWarning;
export const { requestQuotaInfo } = storageQuotaSliceHandler;
